import type { FC, ReactNode } from 'react'
import type { FrameState } from '@/lib/motion/engine'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { engine } from '@/lib/motion/engine'
import { Bracket } from '@/components/landing/primitives'

import {
    getAppState,
    setAppState,
    subscribeAppState
} from '@/lib/app-store'

type Phase = 'loading' | 'closing' | 'opening'

interface ScanPoint {
    angle: number
    radius: number
    threshold: number
    size: number
    alpha: number
    hit: number
}

interface Tween {
    from: number
    to: number
    begin: number
    duration: number
    steps: number
    resolve: () => void
}

const STAGES = [
    { at: 0, label: 'Warming up the sensor' },
    { at: 54, label: 'Aligning the inertial unit' },
    { at: 72, label: 'Locking satellites' },
    { at: 100, label: 'Cleared to fly' }
]

const TAU = Math.PI * 2
const POINT_COUNT = 1500
const SWEEP_PERIOD = 1.7
const SWEEP_TRAIL = 1.15
const TRAIL_SLICES = 22
const TICKS = 72
const INK = '#1d2a31'
const SIGNAL = '#5fbfd0'
const SEAM_EASE = 'cubic-bezier(0.65, 0, 0.35, 1)'
const SPLIT_EASE = 'cubic-bezier(0.76, 0, 0.24, 1)'

/** A ragged perimeter, like the walls and ridges a LiDAR head sees around itself. */
const contour = (angle: number): number => {
    return 0.64 + 0.13 * Math.sin(angle * 3 + 1.2) + 0.07 * Math.sin(angle * 7 - 0.4) + 0.035 * Math.sin(angle * 17 + 2.1)
}

const createPoints = (): Array<ScanPoint> => {
    const points: Array<ScanPoint> = []

    for (let index = 0; index < POINT_COUNT; index++) {
        const kind = Math.random()
        const angle = Math.random() * TAU
        const edge = contour(angle)

        let radius = 0
        let alpha = 0
        let size = 0

        if (kind < 0.42) {
            const ring = 1 + Math.floor(Math.random() * 9)

            radius = (ring / 10) * edge * 0.96 + (Math.random() - 0.5) * 0.008
            alpha = 0.34
            size = 1
        }
        else if (kind < 0.86) {
            radius = edge + (Math.random() - 0.5) * 0.035 + Math.pow(Math.random(), 3) * 0.06
            alpha = 0.8
            size = 1.4
        }
        else {
            radius = edge + 0.04 + Math.random() * Math.max(0.02, 0.95 - edge - 0.04)
            alpha = 0.42
            size = 1.1
        }

        points.push({
            angle,
            radius: Math.min(0.96, Math.max(0.02, radius)),
            threshold: Math.random() * 0.94,
            size,
            alpha,
            hit: -1
        })
    }

    return points
}

const Preloader: FC = (): ReactNode => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const numberRef = useRef<HTMLSpanElement>(null)
    const barRef = useRef<HTMLSpanElement>(null)
    const pointsRef = useRef<HTMLSpanElement>(null)
    const [phase, setPhase] = useState<Phase>('loading')
    const [stage, setStage] = useState(0)
    const [reduced, setReduced] = useState(false)
    const [gone, setGone] = useState(false)

    useEffect(() => {
        const timers: Array<number> = []
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const points = createPoints()
        const context = canvasRef.current?.getContext('2d') ?? null

        let cancelled = false
        let stopped = false
        let tween: Tween | null = null
        let value = 0
        let visual = 0
        let shownValue = -1
        let shownStage = 0
        let sweep = -Math.PI / 2
        let readyAt = -1
        let hits = 0
        let textClock = 0
        let size = 0
        let dpr = 1

        setReduced(reduce)

        const measure = (): void => {
            const canvas = canvasRef.current

            if (!canvas) return

            size = canvas.clientWidth
            dpr = Math.min(2, window.devicePixelRatio || 1)

            const pixels = Math.max(1, Math.round(size * dpr))

            if (canvas.width !== pixels) {
                canvas.width = pixels
                canvas.height = pixels
            }
        }

        const draw = (time: number): void => {
            if (!context || size <= 0) return

            const center = size / 2
            const radius = center - 4

            context.setTransform(dpr, 0, 0, dpr, 0, 0)
            context.clearRect(0, 0, size, size)
            context.lineWidth = 1

            /* Range rings and crosshair */

            context.strokeStyle = 'rgba(29, 42, 49, 0.1)'

            for (const ring of [1, 0.66, 0.33]) {
                context.beginPath()
                context.arc(center, center, radius * ring, 0, TAU)
                context.stroke()
            }

            context.strokeStyle = 'rgba(29, 42, 49, 0.07)'
            context.beginPath()
            context.moveTo(center - radius, center)
            context.lineTo(center + radius, center)
            context.moveTo(center, center - radius)
            context.lineTo(center, center + radius)
            context.stroke()

            /* Calibration ticks light up with the count */

            const lit = new Path2D()
            const dim = new Path2D()

            for (let index = 0; index < TICKS; index++) {
                const angle = -Math.PI / 2 + (index / TICKS) * TAU
                const length = index % 6 === 0 ? 10 : 5
                const path = index / TICKS < visual ? lit : dim
                const cos = Math.cos(angle)
                const sin = Math.sin(angle)

                path.moveTo(center + cos * (radius - length), center + sin * (radius - length))
                path.lineTo(center + cos * radius, center + sin * radius)
            }

            context.strokeStyle = 'rgba(29, 42, 49, 0.16)'
            context.stroke(dim)
            context.strokeStyle = 'rgba(29, 42, 49, 0.78)'
            context.stroke(lit)

            /* The sweep and its fading trail */

            if (!reduce) {
                for (let slice = 0; slice < TRAIL_SLICES; slice++) {
                    const start = sweep - SWEEP_TRAIL * ((slice + 1) / TRAIL_SLICES)
                    const end = sweep - SWEEP_TRAIL * (slice / TRAIL_SLICES)

                    context.fillStyle = `rgba(143, 227, 240, ${(0.3 * Math.pow(1 - slice / TRAIL_SLICES, 1.7)).toFixed(3)})`
                    context.beginPath()
                    context.moveTo(center, center)
                    context.arc(center, center, radius, start, end + 0.004)
                    context.closePath()
                    context.fill()
                }

                context.strokeStyle = 'rgba(29, 42, 49, 0.55)'
                context.beginPath()
                context.moveTo(center, center)
                context.lineTo(center + Math.cos(sweep) * radius, center + Math.sin(sweep) * radius)
                context.stroke()
            }

            /* Returns: settled points in ink, fresh hits in signal cyan */

            context.fillStyle = INK

            for (const point of points) {
                if (point.hit < 0) continue

                const fade = Math.min(1, (time - point.hit) / 0.2)
                const x = center + Math.cos(point.angle) * point.radius * radius
                const y = center + Math.sin(point.angle) * point.radius * radius

                context.globalAlpha = point.alpha * fade
                context.fillRect(x - point.size / 2, y - point.size / 2, point.size, point.size)
            }

            context.fillStyle = SIGNAL

            for (const point of points) {
                if (point.hit < 0) continue

                const fresh = 1 - (time - point.hit) / 0.9

                if (fresh <= 0) continue

                const x = center + Math.cos(point.angle) * point.radius * radius
                const y = center + Math.sin(point.angle) * point.radius * radius
                const dot = point.size + 1

                context.globalAlpha = fresh * 0.9
                context.fillRect(x - dot / 2, y - dot / 2, dot, dot)
            }

            context.globalAlpha = 1

            /* Centre mark, and one pulse outwards once everything is locked */

            context.fillStyle = INK
            context.beginPath()
            context.arc(center, center, 2.2, 0, TAU)
            context.fill()

            if (readyAt >= 0) {
                const t = Math.min(1, (time - readyAt) / 0.9)

                context.strokeStyle = `rgba(95, 191, 208, ${(0.85 * (1 - t)).toFixed(3)})`
                context.beginPath()
                context.arc(center, center, Math.max(1, radius * (0.12 + 0.88 * (1 - Math.pow(1 - t, 3)))), 0, TAU)
                context.stroke()
            }
        }

        const frame = (state: FrameState): void => {
            const now = performance.now()

            /* Stepped count, the same rhythm as before: 0, 54, 72, 100 */

            if (tween) {
                const t = Math.min(1, (now - tween.begin) / tween.duration)
                const eased = 1 - Math.pow(1 - t, 2.2)
                const step = t >= 1 ? 1 : Math.floor(eased * tween.steps) / tween.steps

                value = Math.round(tween.from + (tween.to - tween.from) * step)

                if (t >= 1) {
                    const done = tween.resolve

                    tween = null
                    done()
                }
            }

            if (value !== shownValue) {
                shownValue = value

                if (numberRef.current) numberRef.current.textContent = String(value)

                let next = 0

                for (let index = 0; index < STAGES.length; index++) {
                    if (value >= STAGES[index].at) next = index
                }

                if (next !== shownStage) {
                    shownStage = next
                    setStage(next)
                }

                if (value >= 100 && readyAt < 0) readyAt = state.time
            }

            visual += (value / 100 - visual) * (1 - Math.exp(-7 * state.dt))

            if (barRef.current) barRef.current.style.transform = `scaleX(${visual.toFixed(4)})`

            /* The sweep catches every point that has become available since it last passed */

            const previous = sweep

            if (!reduce) sweep += (TAU / SWEEP_PERIOD) * state.dt

            const span = sweep - previous

            for (const point of points) {
                if (point.hit >= 0 || point.threshold > visual) continue

                const offset = ((point.angle - previous) % TAU + TAU) % TAU

                if (reduce || offset <= span) {
                    point.hit = state.time
                    hits += 1
                }
            }

            textClock += state.dt

            if (textClock > 0.09 && pointsRef.current) {
                textClock = 0
                pointsRef.current.textContent = (hits * 163).toLocaleString('en-US')
            }

            draw(state.time)
        }

        const offMeasure = engine.onMeasure(measure)
        const offFrame = engine.subscribe(frame, -10)

        measure()

        const stop = (): void => {
            if (stopped) return

            stopped = true
            offFrame()
            offMeasure()
        }

        const wait = (ms: number): Promise<void> => new Promise((resolve) => {
            timers.push(window.setTimeout(resolve, ms))
        })

        const count = (to: number, duration: number, steps: number): Promise<void> => new Promise((resolve) => {
            tween = { from: value, to, begin: performance.now(), duration, steps, resolve }
        })

        const stageReady = (): Promise<void> => new Promise((resolve) => {
            if (getAppState().stageReady) {
                resolve()

                return
            }

            const off = subscribeAppState(() => {
                if (!getAppState().stageReady) return

                off()
                resolve()
            })

            timers.push(window.setTimeout(() => {
                off()
                resolve()
            }, 6500))
        })

        const run = async (): Promise<void> => {
            await wait(220)
            await count(54, reduce ? 200 : 1000, 9)
            await wait(reduce ? 40 : 320)
            await count(72, reduce ? 150 : 600, 4)
            await Promise.all([wait(reduce ? 40 : 460), stageReady()])
            await count(100, reduce ? 150 : 760, 7)
            await wait(reduce ? 60 : 680)

            if (cancelled) return

            setPhase('closing')

            await wait(reduce ? 0 : 640)

            if (cancelled) return

            setPhase('opening')
            setAppState({ loaded: true })

            timers.push(window.setTimeout(() => {
                stop()
                setGone(true)
            }, reduce ? 700 : 1450))
        }

        run()

        return () => {
            cancelled = true
            stop()
            timers.forEach(id => window.clearTimeout(id))
        }
    }, [])

    if (gone) return null

    const split = phase === 'opening' && !reduced
    const seam = phase === 'loading' ? 0 : 1

    return (
        <div
            role='status'
            aria-label='Loading Orvane'
            aria-busy={phase === 'loading'}
            className={cn('preloader fixed inset-0 z-[150] overflow-hidden', phase === 'opening' && 'pointer-events-none')}
            style={{
                opacity: phase === 'opening' && reduced ? 0 : 1,
                transition: 'opacity 0.6s ease'
            }}
        >
            <div
                aria-hidden='true'
                className='absolute inset-x-0 top-0 h-1/2 bg-[#fbfcfc] will-change-transform'
                style={{
                    transform: split ? 'translate3d(0, -100%, 0)' : 'translate3d(0, 0, 0)',
                    transition: `transform 1.25s ${SPLIT_EASE}`
                }}
            >
                <span
                    className='absolute inset-x-0 bottom-0 h-px origin-center bg-[#5fbfd0]'
                    style={{ transform: `scaleX(${seam})`, transition: `transform 0.62s ${SEAM_EASE}` }}
                />
            </div>

            <div
                aria-hidden='true'
                className='absolute inset-x-0 bottom-0 h-1/2 bg-[#fbfcfc] will-change-transform'
                style={{
                    transform: split ? 'translate3d(0, 100%, 0)' : 'translate3d(0, 0, 0)',
                    transition: `transform 1.25s ${SPLIT_EASE}`
                }}
            >
                <span
                    className='absolute inset-x-0 top-0 h-px origin-center bg-[#5fbfd0]'
                    style={{ transform: `scaleX(${seam})`, transition: `transform 0.62s ${SEAM_EASE}` }}
                />
            </div>

            <div
                className='absolute inset-0 flex flex-col justify-between px-5 pb-6 pt-5 transition-opacity duration-500 ease-out md:px-10 md:pb-9 md:pt-7'
                style={{ opacity: phase === 'loading' ? 1 : 0 }}
            >
                <div aria-hidden='true' className='pointer-events-none absolute inset-0 flex items-center justify-center'>
                    <canvas
                        ref={canvasRef}
                        className='block size-[min(76vw,44svh)] transition-transform duration-700 ease-out md:size-[min(56vmin,460px)]'
                        style={{ transform: phase === 'loading' ? 'scale(1)' : 'scale(0.96)' }}
                    />
                </div>

                <div className='relative flex items-start justify-between gap-6'>
                    <Bracket className='text-ink'>Orvane L4</Bracket>
                    <Bracket className='text-haze'>Pre-flight check</Bracket>
                </div>

                <div className='relative flex flex-col gap-4'>
                    <div className='flex items-end justify-between gap-6'>
                        <p aria-hidden='true' className='flex items-start text-[clamp(64px,10vw,152px)] font-light leading-[0.8] tracking-[-0.065em] text-ink tabular-nums'>
                            <span ref={numberRef}>0</span>
                            <span className='label-mono ml-2 mt-1 text-haze'>%</span>
                        </p>

                        <div className='flex flex-col items-end gap-1.5 pb-1 text-right'>
                            <span className='label-mono tabular-nums text-haze'>0{stage + 1} / 04</span>

                            <span key={stage} className='label-mono text-ink animate-in fade-in slide-in-from-bottom-1 duration-500'>
                                {STAGES[stage].label}
                            </span>

                            <span aria-hidden='true' className='label-mono tabular-nums text-haze'>
                                Points <span ref={pointsRef}>0</span>
                            </span>
                        </div>
                    </div>

                    <div aria-hidden='true' className='relative h-px w-full overflow-hidden bg-line'>
                        <span ref={barRef} className='absolute inset-0 block origin-left bg-ink' style={{ transform: 'scaleX(0)' }} />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Preloader
